import React, { useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Image as ImageIcon, Upload, FileSpreadsheet, LayoutTemplate,
  Bookmark, ArrowRight, Lightbulb, BookOpen, FilePlus, HelpCircle,
  ChevronDown, X, Smartphone, Box
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'

const CATEGORY_OPTIONS = ['성경', '역사', '과학', '상식', '언어', '문화', '기타']

const Page = styled.div`
  padding: 24px 28px 48px;
  background: ${p => p.theme.backgroundColor};
  min-height: calc(100vh - 60px);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 1100px) { padding: 20px; }
  @media (max-width: 768px) { padding: 14px; }
`

const TopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 28px;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: ${p => p.theme.textPrimary};
  margin: 0;
  letter-spacing: -0.5px;
`

const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${p => p.theme.textSecondary};
  margin: 0;
`

const StepperWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1;
  min-width: 0;
  max-width: 480px;
  justify-content: center;
  margin: 0 10px;

  @media (max-width: 1280px) {
    order: 3;
    width: 100%;
    margin: 10px 0 0;
    max-width: none;
  }
`

const StepDot = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  .num {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11.5px;
    font-weight: 700;
    background: ${p => p.$active ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : p.theme.borderLight};
    color: ${p => p.$active ? '#ffffff' : p.theme.textMuted};
    box-shadow: ${p => p.$active ? '0 4px 10px rgba(124,58,237,0.25)' : 'none'};
    flex-shrink: 0;
  }
  .label {
    font-size: 12px;
    font-weight: 600;
    color: ${p => p.$active ? p.theme.textPrimary : p.theme.textMuted};
  }
`

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${p => p.theme.borderLight};
  margin: 0 8px;
  min-width: 12px;
`

const TopActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`

const SecondaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.border};
  color: ${p => p.theme.textPrimary};
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const PrimaryBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  background: linear-gradient(135deg, #7c3aed 0%, #4f46e5 50%, #2563eb 100%);
  color: #ffffff;
  border: none;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 6px 16px rgba(124,58,237,0.30);
  white-space: nowrap;
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(124,58,237,0.40);
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) 290px;
  gap: 16px;
  align-items: start;
  min-width: 0;
  width: 100%;

  @media (max-width: 1180px) {
    grid-template-columns: 240px minmax(0, 1fr);
  }
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

const Card = styled.div`
  background: ${p => p.theme.cardBackground};
  border: 1px solid ${p => p.theme.border};
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  h3 {
    font-size: 15px;
    font-weight: 700;
    color: ${p => p.theme.textPrimary};
    margin: 0;
  }
  span.help {
    color: ${p => p.theme.textMuted};
    display: inline-flex;
    cursor: help;
  }
`

const CoverDropZone = styled.div`
  border: 2px dashed ${p => p.$drag ? '#7c3aed' : p.theme.border};
  border-radius: 14px;
  padding: 24px 16px;
  text-align: center;
  transition: all 0.2s;
  background: ${p => p.$drag ? 'rgba(124,58,237,0.06)' : p.theme.hoverBackground};
  cursor: pointer;
  position: relative;

  &:hover { border-color: #7c3aed; }

  img.cover-preview {
    width: 100%;
    aspect-ratio: 16 / 9;
    object-fit: cover;
    border-radius: 10px;
    display: block;
  }
`

const DropIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 10px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(124,58,237,0.14), rgba(37,99,235,0.14));
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
`

const DropTitle = styled.div`
  font-size: 13.5px;
  font-weight: 600;
  color: ${p => p.theme.textPrimary};
  margin-bottom: 4px;
`
const DropHint = styled.div`
  font-size: 12px;
  color: ${p => p.theme.textMuted};
  margin-bottom: 12px;
`

const SelectFileBtn = styled.button`
  padding: 7px 14px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.cardBackground};
  color: ${p => p.theme.textPrimary};
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: #7c3aed; color: #7c3aed; }
`

const RemoveCoverBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.55);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover { background: rgba(0,0,0,0.85); }
`

const WebPreview = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.hoverBackground};
`

const WebPreviewImage = styled.div`
  aspect-ratio: 16 / 9;
  background: ${p => p.$image
    ? `url(${p.$image}) center/cover`
    : 'linear-gradient(135deg, #c4b5fd 0%, #93c5fd 100%)'};
`

const WebPreviewBody = styled.div`
  padding: 14px;

  h4 {
    font-size: 14px;
    font-weight: 700;
    color: ${p => p.theme.textPrimary};
    margin: 0 0 6px;
  }
  p {
    font-size: 12px;
    color: ${p => p.theme.textSecondary};
    margin: 0 0 10px;
    min-height: 18px;
  }
`

const PreviewChips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;

  span {
    font-size: 11px;
    padding: 3px 9px;
    border-radius: 999px;
    background: ${p => p.theme.borderLight};
    color: ${p => p.theme.textSecondary};
  }
`

const TipBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: linear-gradient(135deg, rgba(124,58,237,0.08), rgba(37,99,235,0.08));
  border: 1px solid rgba(124,58,237,0.18);
  border-radius: 12px;
  padding: 12px 14px;

  .icon {
    color: #7c3aed;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .text {
    font-size: 12.5px;
    color: ${p => p.theme.textPrimary};
    line-height: 1.45;
    strong { display: block; font-weight: 700; margin-bottom: 2px; color: #7c3aed; }
  }
`

/* center column */
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  & + & { margin-top: 4px; }
`

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.textPrimary};

  em {
    color: #ef4444;
    font-style: normal;
    margin-left: 2px;
  }
`

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid ${p => p.theme.border};
  border-radius: 10px;
  font-size: 14px;
  background: ${p => p.theme.inputBackground};
  color: ${p => p.theme.textPrimary};
  transition: all 0.15s;

  &::placeholder { color: ${p => p.theme.textMuted}; }
  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.12);
  }
`

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1.5px solid ${p => p.theme.border};
  border-radius: 10px;
  font-size: 14px;
  background: ${p => p.theme.inputBackground};
  color: ${p => p.theme.textPrimary};
  resize: vertical;
  min-height: 90px;
  font-family: inherit;
  transition: all 0.15s;

  &::placeholder { color: ${p => p.theme.textMuted}; }
  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.12);
  }
`

const FieldFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 11.5px;
  color: ${p => p.theme.textMuted};
  margin-top: -2px;
`

const SelectWrap = styled.div`
  position: relative;
  svg.chev { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: ${p => p.theme.textMuted}; pointer-events: none; }
  svg.lead { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: ${p => p.theme.textMuted}; pointer-events: none; }
`

const SelectInput = styled.select`
  width: 100%;
  padding: 12px 36px 12px 38px;
  border: 1.5px solid ${p => p.theme.border};
  border-radius: 10px;
  font-size: 14px;
  background: ${p => p.theme.inputBackground};
  color: ${p => p.theme.textPrimary};
  appearance: none;
  cursor: pointer;
  transition: all 0.15s;
  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.12);
  }
`

const DifficultyRow = styled.div`
  display: flex;
  gap: 8px;
`

const DifficultyBtn = styled.button`
  flex: 1;
  padding: 11px 6px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  background: ${p => p.$active ? p.$activeBg : p.theme.cardBackground};
  color: ${p => p.$active ? '#ffffff' : p.$color};
  border: 1.5px solid ${p => p.$active ? 'transparent' : p.$color};

  &:hover { transform: translateY(-1px); }
`

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const TogglePill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1.5px solid ${p => p.$active ? '#7c3aed' : p.theme.border};
  background: ${p => p.$active ? 'rgba(124,58,237,0.08)' : p.theme.cardBackground};
  color: ${p => p.$active ? '#7c3aed' : p.theme.textSecondary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  .knob {
    width: 28px;
    height: 16px;
    border-radius: 999px;
    background: ${p => p.$active ? '#7c3aed' : p.theme.border};
    position: relative;
    &::after {
      content: '';
      position: absolute;
      top: 2px;
      left: ${p => p.$active ? '14px' : '2px'};
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #ffffff;
      transition: left 0.15s;
    }
  }
`

const VisibilityNote = styled.div`
  font-size: 12px;
  color: ${p => p.theme.textMuted};
  margin-top: -4px;
`

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const MethodCard = styled.button`
  text-align: left;
  padding: 14px;
  border-radius: 12px;
  border: 1.5px solid ${p => p.$active ? '#7c3aed' : p.theme.border};
  background: ${p => p.$active ? 'rgba(124,58,237,0.06)' : p.theme.cardBackground};
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover { border-color: #7c3aed; transform: translateY(-1px); }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${p => p.$bg || 'rgba(124,58,237,0.14)'};
    color: ${p => p.$color || '#7c3aed'};
  }
  .info {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      font-size: 13.5px;
      font-weight: 700;
      color: ${p => p.theme.textPrimary};
      margin-bottom: 2px;
    }
    span {
      font-size: 11.5px;
      color: ${p => p.theme.textSecondary};
      line-height: 1.35;
    }
  }
`

/* Mobile preview */
const MobileFrame = styled.div`
  position: relative;
  width: 100%;
  max-width: 230px;
  aspect-ratio: 240 / 480;
  margin: 0 auto;
  border: 8px solid #1f2937;
  border-radius: 36px;
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0,0,0,0.18);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 90px;
    height: 18px;
    background: #1f2937;
    border-radius: 0 0 14px 14px;
    z-index: 2;
  }
`

const MobileScreen = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #f5f3ff 0%, #ffffff 100%);
  display: flex;
  flex-direction: column;
  padding: 28px 16px 16px;
  align-items: center;
  text-align: center;
`

const MobileLogo = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 800;
  color: #4f46e5;
  margin: 6px 0 14px;
`

const MobileCover = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 14px;
  background: ${p => p.$image ? `url(${p.$image}) center/cover` : 'linear-gradient(135deg, #c4b5fd, #93c5fd)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  margin-bottom: 14px;
`

const MobileTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.3;
  margin-bottom: 4px;
`

const MobileDesc = styled.div`
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
  min-height: 30px;
  margin-bottom: 12px;
`

const MobileMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  width: 100%;
  padding: 7px 4px;
  border-top: 1px solid #f1f5f9;

  span:first-child { color: #64748b; display: inline-flex; align-items: center; gap: 4px; }
  span:last-child { color: #0f172a; font-weight: 700; }
`

const MobileStartBtn = styled.div`
  margin-top: 10px;
  width: 100%;
  padding: 9px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #7c3aed, #2563eb);
  text-align: center;
`

const PreviewNote = styled.div`
  text-align: center;
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  margin-top: 12px;
`

/* Bottom row */
const BottomGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 20px;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  h3 {
    font-size: 15px;
    font-weight: 700;
    color: ${p => p.theme.textPrimary};
    margin: 0;
  }
  a {
    font-size: 12.5px;
    color: #7c3aed;
    cursor: pointer;
    font-weight: 600;
    text-decoration: none;
    &:hover { text-decoration: underline; }
  }
`

const RecentRow = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-thumb { background: ${p => p.theme.border}; border-radius: 3px; }
`

const RecentItem = styled.button`
  flex: 0 0 220px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border-radius: 12px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.cardBackground};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  &:hover { border-color: #7c3aed; transform: translateY(-1px); }

  .thumb {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    flex-shrink: 0;
    background: linear-gradient(135deg, #c4b5fd, #93c5fd);
    background-size: cover;
    background-position: center;
  }
  .info {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      font-size: 13px;
      color: ${p => p.theme.textPrimary};
      margin-bottom: 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .meta {
      font-size: 11px;
      color: ${p => p.theme.textMuted};
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .badge {
      display: inline-block;
      font-size: 10px;
      font-weight: 700;
      padding: 2px 7px;
      border-radius: 999px;
      background: rgba(124,58,237,0.12);
      color: #7c3aed;
      margin-left: 4px;
    }
  }
`

const QuickGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const QuickCard = styled.button`
  text-align: left;
  padding: 14px;
  border-radius: 12px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.cardBackground};
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: #7c3aed; transform: translateY(-1px); }

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${p => p.$bg};
    color: ${p => p.$color};
  }
  .info {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      font-size: 13.5px;
      font-weight: 700;
      color: ${p => p.theme.textPrimary};
      margin-bottom: 3px;
    }
    span {
      font-size: 11.5px;
      color: ${p => p.theme.textSecondary};
      line-height: 1.35;
    }
  }
`

/* Modal */
const ModalBackdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(15,15,30,0.55);
  display: ${p => p.$open ? 'flex' : 'none'};
  align-items: center; justify-content: center;
  z-index: 9999;
`
const ModalBox = styled.div`
  background: ${p => p.theme.cardBackground};
  border-radius: 16px;
  padding: 28px 26px 22px;
  width: min(90%, 380px);
  text-align: center;

  h4 { margin: 0 0 8px; font-size: 17px; color: ${p => p.theme.textPrimary}; }
  p { margin: 0 0 18px; font-size: 13px; color: ${p => p.theme.textSecondary}; line-height: 1.5; }
  button {
    padding: 10px 22px;
    border-radius: 10px;
    border: none;
    background: linear-gradient(135deg, #7c3aed, #4f46e5);
    color: #fff;
    font-weight: 700;
    cursor: pointer;
  }
`

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '알 수 없음'
  const now = new Date()
  const date = new Date(timestamp)
  const diffSec = Math.floor((now - date) / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)
  if (diffSec < 60) return '방금 전'
  if (diffMin < 60) return `${diffMin}분 전`
  if (diffHour < 24) return `${diffHour}시간 전`
  if (diffDay < 7) return `${diffDay}일 전`
  if (diffDay < 30) return `${Math.floor(diffDay / 7)}주 전`
  if (diffDay < 365) return `${Math.floor(diffDay / 30)}개월 전`
  return `${Math.floor(diffDay / 365)}년 전`
}

const parseCSV = (text) => {
  const lines = text.split('\n').filter(l => l.trim() !== '')
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''))
  const data = []
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''))
    if (values.length === headers.length) {
      const row = {}
      headers.forEach((h, idx) => { row[h] = values[idx] })
      data.push(row)
    }
  }
  return data
}

export default function CreatePage() {
  const { theme } = useTheme()
  const { isLoggedIn, user } = useAuth()
  const navigate = useNavigate()
  const fileInputRef = useRef(null)
  const coverInputRef = useRef(null)

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [difficulty, setDifficulty] = useState('보통')
  const [isPrivate, setIsPrivate] = useState(false)
  const [coverImage, setCoverImage] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [csvData, setCsvData] = useState(null)
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [addMethod, setAddMethod] = useState('direct') // direct | excel | template
  const [recentProjects, setRecentProjects] = useState([])
  const [showModal, setShowModal] = useState(false)

  // 최근 작업 불러오기
  useEffect(() => {
    if (!isLoggedIn) return
    fetch('/auth/get-my-quizzes', { method: 'GET', credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success && data.myQuizzes) {
          const sorted = data.myQuizzes
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 6)
            .map(q => ({
              id: q.set_id,
              title: q.set_title,
              cover: q.set_cover_img,
              status: q.status || '작성중',
              questions: q.set_obj_count || 0,
              lastEdited: formatTimeAgo(q.created_at),
            }))
          setRecentProjects(sorted)
        }
      })
      .catch(err => console.error('최근 퀴즈 로드 실패:', err))
  }, [isLoggedIn])

  /* Cover image */
  const readCoverFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return
    const reader = new FileReader()
    reader.onload = (e) => setCoverImage(e.target.result)
    reader.readAsDataURL(file)
  }

  const handleCoverInputChange = (e) => {
    const f = e.target.files?.[0]
    if (f) readCoverFile(f)
  }

  const handleCoverDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) readCoverFile(f)
  }

  const handleRemoveCover = (e) => {
    e.stopPropagation()
    setCoverImage(null)
    if (coverInputRef.current) coverInputRef.current.value = ''
  }

  /* Excel/CSV upload */
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
      alert('Excel(.xlsx, .xls) 또는 CSV(.csv) 파일만 업로드 가능합니다.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('파일 크기는 10MB 이하만 업로드 가능합니다.')
      return
    }

    if (ext === '.csv') {
      const reader = new FileReader()
      reader.onload = (evt) => {
        try {
          const parsed = parseCSV(evt.target.result)
          setCsvData(parsed)
          setUploadedFileName(file.name)
          if (!title) setTitle(file.name.replace('.csv', ''))
        } catch (err) {
          console.error('CSV 파싱 실패:', err)
          alert('CSV 파싱에 실패했습니다.')
        }
      }
      reader.readAsText(file)
    } else {
      const reader = new FileReader()
      reader.onload = (evt) => {
        try {
          const wb = XLSX.read(evt.target.result, { type: 'array' })
          const ws = wb.Sheets[wb.SheetNames[0]]
          const json = XLSX.utils.sheet_to_json(ws)
          setCsvData(json)
          setUploadedFileName(file.name)
          if (!title) setTitle(file.name.replace(/\.(xlsx|xls)$/, ''))
        } catch (err) {
          console.error('Excel 파싱 실패:', err)
          alert('Excel 파싱에 실패했습니다.')
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  const downloadExcelTemplate = () => {
    const data = [
      ['문제번호', '문제내용', '선택지1', '선택지2', '선택지3', '선택지4', '정답번호', '오브젝트번호'],
      ['1', '예시 문제입니다', '선택지 1', '선택지 2', '선택지 3', '선택지 4', '1', '1'],
      ['2', '', '', '', '', '', '', ''],
    ]
    const ws = XLSX.utils.aoa_to_sheet(data)
    ws['!cols'] = [{ wch: 10 }, { wch: 40 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 10 }, { wch: 12 }]
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, '퀴즈 템플릿')
    XLSX.writeFile(wb, 'quiz_template.xlsx')
  }

  /* Next step */
  const handleNext = async () => {
    if (!isLoggedIn) {
      alert('로그인이 필요한 기능입니다.')
      navigate('/login')
      return
    }
    if (!title.trim()) {
      setShowModal(true)
      return
    }
    if (!user || !user.id) {
      alert('사용자 정보를 찾을 수 없습니다. 다시 로그인해주세요.')
      navigate('/login')
      return
    }

    try {
      if (csvData && csvData.length > 0) {
        navigate('/createdetail', {
          state: {
            title: title.trim(),
            description: description.trim(),
            category, difficulty,
            isPrivate,
            coverImage,
            questions: csvData,
            isFromExcel: true,
          },
        })
      } else {
        const res = await fetch('/auth/create-quiz-basic', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            title: title.trim(),
            description: description.trim(),
            category, difficulty,
            isPrivate,
            coverImage,
            userId: user.id,
          }),
        })
        const result = await res.json()
        if (result.success) {
          navigate('/createdetail', {
            state: { title, description, category, difficulty, isPrivate, questionId: result.questionId },
          })
        } else {
          alert(result.message || 'DB 저장에 실패했습니다.')
        }
      }
    } catch (err) {
      console.error('저장 중 오류:', err)
      alert('저장 중 오류가 발생했습니다.')
    }
  }

  const handleSaveDraft = async () => {
    if (!title.trim()) {
      alert('제목을 입력하면 임시저장할 수 있습니다.')
      return
    }
    alert('임시저장되었습니다.')
  }

  /* Method card click */
  const handleMethodSelect = (method) => {
    setAddMethod(method)
    if (method === 'excel') {
      fileInputRef.current?.click()
    } else if (method === 'template') {
      downloadExcelTemplate()
    }
  }

  return (
    <Page theme={theme}>
      <TopBar>
        <TitleBlock>
          <PageTitle theme={theme}>퀴즈 만들기 <span style={{ color: '#7c3aed' }}>✨</span></PageTitle>
          <PageSubtitle theme={theme}>나만의 퀴즈를 만들어 학생들과 함께 즐겨보세요!</PageSubtitle>
        </TitleBlock>

        <StepperWrap>
          {[
            { n: 1, label: '기본 정보', active: true },
            { n: 2, label: '오브젝트 등록' },
            { n: 3, label: '문제 만들기' },
            { n: 4, label: '배포하기' },
          ].map((s, i, arr) => (
            <React.Fragment key={s.n}>
              <StepDot $active={s.active} theme={theme}>
                <span className="num">{s.n}</span>
                <span className="label">{s.label}</span>
              </StepDot>
              {i < arr.length - 1 && <StepLine theme={theme} />}
            </React.Fragment>
          ))}
        </StepperWrap>

        <TopActions>
          <SecondaryBtn theme={theme} onClick={handleSaveDraft}>
            <Bookmark size={15} /> 임시저장
          </SecondaryBtn>
          <PrimaryBtn onClick={handleNext}>
            다음 단계 <ArrowRight size={15} />
          </PrimaryBtn>
        </TopActions>
      </TopBar>

      <MainGrid>
        {/* LEFT: Cover */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Card theme={theme}>
            <CardHeader theme={theme}>
              <h3>커버 이미지</h3>
              <span className="help" title="권장 크기: 1280x720 (16:9)"><HelpCircle size={14} /></span>
            </CardHeader>

            <CoverDropZone
              theme={theme}
              $drag={isDragOver}
              onClick={() => coverInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleCoverDrop}
            >
              {coverImage ? (
                <>
                  <img src={coverImage} alt="cover" className="cover-preview" />
                  <RemoveCoverBtn onClick={handleRemoveCover} aria-label="이미지 제거">
                    <X size={14} />
                  </RemoveCoverBtn>
                </>
              ) : (
                <>
                  <DropIcon><ImageIcon size={26} /></DropIcon>
                  <DropTitle theme={theme}>이미지를 업로드하세요</DropTitle>
                  <DropHint theme={theme}>권장 크기 1280 × 720px (16:9)</DropHint>
                  <SelectFileBtn theme={theme} onClick={(e) => { e.stopPropagation(); coverInputRef.current?.click() }}>
                    이미지 선택
                  </SelectFileBtn>
                </>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleCoverInputChange}
              />
            </CoverDropZone>
          </Card>

          <Card theme={theme}>
            <CardHeader theme={theme}><h3>미리보기 (웹)</h3></CardHeader>
            <WebPreview theme={theme}>
              <WebPreviewImage $image={coverImage} theme={theme} />
              <WebPreviewBody theme={theme}>
                <h4>{title || '퀴즈 제목이 여기에 표시됩니다'}</h4>
                <p>{description || '퀴즈 설명이 여기에 표시됩니다.'}</p>
                <PreviewChips theme={theme}>
                  <span>{category || '카테고리'}</span>
                  <span>{difficulty}</span>
                  <span>문제 {csvData?.length || 0}개</span>
                </PreviewChips>
              </WebPreviewBody>
            </WebPreview>
          </Card>

          <TipBox theme={theme}>
            <Lightbulb size={16} className="icon" />
            <div className="text">
              <strong>TIP</strong>
              매력적인 커버 이미지는 학생들의 참여율을 높여줍니다!
            </div>
          </TipBox>
        </div>

        {/* CENTER: Form */}
        <Card theme={theme}>
          <CardHeader theme={theme}>
            <h3>기본 정보</h3>
          </CardHeader>
          <PageSubtitle theme={theme} style={{ marginTop: -12, marginBottom: 4 }}>
            퀴즈의 기본 정보를 입력해주세요.
          </PageSubtitle>

          <Field>
            <FieldLabel theme={theme}>제목<em>*</em></FieldLabel>
            <Input
              theme={theme}
              type="text"
              placeholder="퀴즈 제목을 입력하세요."
              value={title}
              maxLength={50}
              onChange={(e) => setTitle(e.target.value)}
            />
            <FieldFooter theme={theme}>{title.length} / 50</FieldFooter>
          </Field>

          <Field>
            <FieldLabel theme={theme}>설명</FieldLabel>
            <Textarea
              theme={theme}
              placeholder="퀴즈에 대한 설명을 입력하세요. (선택사항)"
              value={description}
              maxLength={200}
              onChange={(e) => setDescription(e.target.value)}
            />
            <FieldFooter theme={theme}>{description.length} / 200</FieldFooter>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field>
              <FieldLabel theme={theme}>카테고리<em>*</em></FieldLabel>
              <SelectWrap theme={theme}>
                <Box size={16} className="lead" />
                <ChevronDown size={16} className="chev" />
                <SelectInput
                  theme={theme}
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">카테고리를 선택하세요.</option>
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </SelectInput>
              </SelectWrap>
            </Field>

            <Field>
              <FieldLabel theme={theme}>난이도<em>*</em></FieldLabel>
              <DifficultyRow>
                <DifficultyBtn
                  type="button"
                  theme={theme}
                  $color="#10b981"
                  $activeBg="linear-gradient(135deg, #34d399, #10b981)"
                  $active={difficulty === '쉬움'}
                  onClick={() => setDifficulty('쉬움')}
                >쉬움</DifficultyBtn>
                <DifficultyBtn
                  type="button"
                  theme={theme}
                  $color="#f59e0b"
                  $activeBg="linear-gradient(135deg, #fbbf24, #f59e0b)"
                  $active={difficulty === '보통'}
                  onClick={() => setDifficulty('보통')}
                >보통</DifficultyBtn>
                <DifficultyBtn
                  type="button"
                  theme={theme}
                  $color="#ef4444"
                  $activeBg="linear-gradient(135deg, #f87171, #ef4444)"
                  $active={difficulty === '어려움'}
                  onClick={() => setDifficulty('어려움')}
                >어려움</DifficultyBtn>
              </DifficultyRow>
            </Field>
          </div>

          <Field>
            <FieldLabel theme={theme}>공개 여부 설정</FieldLabel>
            <VisibilityNote theme={theme}>완성된 퀴즈를 다른 선생님들과 공유할 수 있습니다.</VisibilityNote>
            <ToggleRow>
              <TogglePill type="button" theme={theme} $active={!isPrivate} onClick={() => setIsPrivate(false)}>
                <span className="knob" />
                공개
              </TogglePill>
              <TogglePill type="button" theme={theme} $active={isPrivate} onClick={() => setIsPrivate(true)}>
                <span className="knob" />
                비공개
              </TogglePill>
            </ToggleRow>
          </Field>

          <Field>
            <FieldLabel theme={theme}>문제 추가 방법 선택</FieldLabel>
            <MethodGrid>
              <MethodCard
                type="button"
                theme={theme}
                $active={addMethod === 'direct'}
                $bg="rgba(124,58,237,0.14)"
                $color="#7c3aed"
                onClick={() => handleMethodSelect('direct')}
              >
                <div className="icon"><Plus size={20} /></div>
                <div className="info">
                  <strong>직접 추가</strong>
                  <span>하나씩 직접 문제를 만들어요.</span>
                </div>
              </MethodCard>

              <MethodCard
                type="button"
                theme={theme}
                $active={addMethod === 'excel'}
                $bg="rgba(16,185,129,0.14)"
                $color="#10b981"
                onClick={() => handleMethodSelect('excel')}
              >
                <div className="icon"><FileSpreadsheet size={20} /></div>
                <div className="info">
                  <strong>Excel 업로드</strong>
                  <span>{uploadedFileName || 'Excel 파일을 업로드하여 문제를 가져와요.'}</span>
                </div>
              </MethodCard>

              <MethodCard
                type="button"
                theme={theme}
                $active={addMethod === 'template'}
                $bg="rgba(124,58,237,0.14)"
                $color="#7c3aed"
                onClick={() => handleMethodSelect('template')}
              >
                <div className="icon"><LayoutTemplate size={20} /></div>
                <div className="info">
                  <strong>템플릿 사용</strong>
                  <span>다양한 템플릿을 활용해 빠르게 만들어보세요.</span>
                </div>
              </MethodCard>
            </MethodGrid>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Field>
        </Card>

        {/* RIGHT: Mobile preview */}
        <Card theme={theme}>
          <CardHeader theme={theme}>
            <h3>미리보기 (모바일)</h3>
            <span className="help" title="모바일 앱에서 어떻게 보이는지 확인할 수 있어요"><HelpCircle size={14} /></span>
          </CardHeader>

          <MobileFrame>
            <MobileScreen>
              <MobileLogo>
                <Smartphone size={12} /> AR 대탈출
              </MobileLogo>
              <MobileCover $image={coverImage}>
                {!coverImage && <Box size={36} />}
              </MobileCover>
              <MobileTitle>{title || '퀴즈 제목이\n여기에 표시됩니다'}</MobileTitle>
              <MobileDesc>{description || '퀴즈 설명이 여기에 표시됩니다.'}</MobileDesc>
              <MobileMetaRow>
                <span><Box size={11} /> 카테고리</span>
                <span>{category || '선택됨'}</span>
              </MobileMetaRow>
              <MobileMetaRow>
                <span>난이도</span>
                <span>{difficulty}</span>
              </MobileMetaRow>
              <MobileMetaRow>
                <span>문제 수</span>
                <span>{csvData?.length || 0}문제</span>
              </MobileMetaRow>
              <MobileStartBtn>퀴즈 시작하기</MobileStartBtn>
            </MobileScreen>
          </MobileFrame>

          <PreviewNote theme={theme}>실제 모바일 화면과 다를 수 있습니다.</PreviewNote>
        </Card>
      </MainGrid>

      <BottomGrid>
        <Card theme={theme}>
          <SectionHeader theme={theme}>
            <h3>최근 작업한 퀴즈</h3>
            {recentProjects.length > 0 && (
              <a onClick={() => navigate('/myset')}>전체 보기 →</a>
            )}
          </SectionHeader>
          {recentProjects.length === 0 ? (
            <PageSubtitle theme={theme}>아직 작업한 퀴즈가 없어요. 위에서 새로 만들어 보세요.</PageSubtitle>
          ) : (
            <RecentRow theme={theme}>
              {recentProjects.map(p => (
                <RecentItem key={p.id} theme={theme} onClick={() => navigate(`/create/edit/${p.id}`)}>
                  <div
                    className="thumb"
                    style={p.cover ? { backgroundImage: `url(${p.cover})` } : {}}
                  />
                  <div className="info">
                    <strong>{p.title}</strong>
                    <div className="meta">
                      수정: {p.lastEdited}
                      <span className="badge">{p.status}</span>
                    </div>
                  </div>
                </RecentItem>
              ))}
            </RecentRow>
          )}
        </Card>

      </BottomGrid>

      <ModalBackdrop $open={showModal} onClick={() => setShowModal(false)}>
        <ModalBox theme={theme} onClick={(e) => e.stopPropagation()}>
          <h4>제목을 입력해주세요</h4>
          <p>퀴즈 제목은 필수입니다.<br />간단한 제목을 입력하고 다시 시도해주세요.</p>
          <button onClick={() => setShowModal(false)}>확인</button>
        </ModalBox>
      </ModalBackdrop>
    </Page>
  )
}
